import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import {
  Alert,
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import Swal from "sweetalert2";

export default function Issue() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const isInvalidSession =
    !clientId ||
    !collegeId ||
    clientId === "null" ||
    collegeId === "null" ||
    isNaN(Number(clientId)) ||
    isNaN(Number(collegeId));

  const [idError, setIdError] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    user_type_id: "",
    academic_year: "",
    issue_type: "SINGLE",
    entered_id: "",
    user_id: null,
    books: [],
  });
  const [userTypes, setUserTypes] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [scanBookIsbn, setScanBookIsbn] = useState("");
  const [isbnFieldError, setIsbnFieldError] = useState("");
  const [idFieldError, setIdFieldError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [rules, setRules] = useState(null);
  const [userIdValidated, setUserIdValidated] = useState(false);

  useEffect(() => {
    if (!clientId || !collegeId || clientId === "null" || collegeId === "null") {
      setIdError("Missing or invalid client/college ID. Please log in again or contact admin.");
    } else {
      setIdError("");
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    const swalStyle = document.createElement("style");
    swalStyle.innerHTML = `.swal2-z-topmost { z-index: 9999 !important; } .swal2-container { z-index: 9999 !important; }`;
    swalStyle.setAttribute("data-swal-zindex", "true");
    if (!document.head.querySelector("[data-swal-zindex]")) {
      document.head.appendChild(swalStyle);
    }
    return () => {
      if (swalStyle.parentNode) swalStyle.parentNode.removeChild(swalStyle);
    };
  }, []);

  // Fetch rules when required fields are filled and user_id is validated
  useEffect(() => {
    const fetchRules = async () => {
      if (
        !clientId ||
        !form.user_id ||
        !form.user_type_id ||
        !form.academic_year ||
        !form.issue_type
      ) {
        setRules(null);
        return;
      }
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/library/issue/rules/${clientId}/${form.user_id}` +
            `?user_type_id=${form.user_type_id}&academic_year=${form.academic_year}&issue_type=${form.issue_type}`
        );
        if (res.data.success && res.data.data) {
          setRules(res.data.data);
        } else {
          setRules(null);
        }
      } catch {
        setRules(null);
      }
    };
    fetchRules();
  }, [
    form.user_id,
    form.user_type_id,
    form.academic_year,
    form.issue_type,
    API_BASE_URL,
    clientId,
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}`
      );
      const items = Array.isArray(response.data.data?.items)
        ? response.data.data.items.map((row) => {
            const userTypeObj = userTypes.find(
              (ut) => String(ut.user_type_id) === String(row.user_type_id)
            );
            const books = Array.isArray(row.issue_details)
              ? row.issue_details.map((d) => ({
                  copy_id: d.copy_id,
                  isbn: d.copy?.book?.isbn,
                  title: d.copy?.book?.title,
                  accession_no: d.copy?.accession_no,
                }))
              : [];
            return {
              ...row,
              id: row.issue_id,
              user_type: userTypeObj ? userTypeObj.user_type_name : row.user_type_id,
              student_id: row.user_id || "",
              books,
            };
          })
        : [];
      setRows(items);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch issues",
        severity: "error",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTypes = async () => {
    if (!clientId || !collegeId) return setUserTypes([]);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/user-types`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setUserTypes(res.data.data);
      } else {
        setUserTypes([]);
      }
    } catch (err) {
      setUserTypes([]);
    }
  };

  const fetchAcademicYears = async () => {
    if (!clientId || !collegeId) return setAcademicYears([]);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/academic-years`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setAcademicYears(res.data.data);
      } else {
        setAcademicYears([]);
      }
    } catch (err) {
      setAcademicYears([]);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData();
      fetchUserTypes();
      fetchAcademicYears();
    }
  }, [clientId, collegeId]);

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}`
      );
      const items = Array.isArray(response.data.data?.items) ? response.data.data.items : [];
      if (!items.length) {
        alert("No issue data to export.");
        return;
      }
      const headers = [
        "issue_id",
        "User Type",
        "Issue Type",
        "User ID",
        "Issue Date",
        "Due Date",
        "Books",
      ];
      const csvRows = [headers.join(",")];
      items.forEach((row) => {
        const booksStr = (row.issue_details || []).map((b) => b.copy?.book?.title || "").join(";");
        csvRows.push(
          [
            row.issue_id || "",
            `"${row.user_type_id || ""}"`,
            `"${row.issue_type || ""}"`,
            `"${row.user_id || ""}"`,
            `"${row.issue_date ? new Date(row.issue_date).toLocaleDateString() : ""}"`,
            `"${row.due_date ? new Date(row.due_date).toLocaleDateString() : ""}"`,
            `"${booksStr}"`,
          ].join(",")
        );
      });
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "issues.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download issue data as CSV.");
    }
  };

  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/upload/csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setSnackbar({
        open: true,
        message: data.message || "CSV uploaded successfully",
        severity: "success",
      });
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({
      user_type_id: "",
      academic_year: "",
      issue_type: "SINGLE",
      entered_id: "",
      user_id: null,
      books: [],
    });
    setOpen(true);
    setScanBookIsbn("");
    setUserIdValidated(false);
    setRules(null);
  };

  const handleClose = () => setOpen(false);

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "user_type_id") {
      setForm((prev) => ({ ...prev, entered_id: "", user_id: null }));
      setIdFieldError("");
      setUserIdValidated(false);
      setRules(null);
    }
    if (e.target.name === "entered_id") {
      setIdFieldError("");
      setUserIdValidated(false);
    }
    if (e.target.name === "academic_year" || e.target.name === "issue_type") {
      setUserIdValidated(false);
    }
  };

  const handleIdFieldBlur = async () => {
    if (!form.user_type_id || !form.entered_id) return;

    let url = "";
    let label = "";
    const selectedType = userTypes.find(
      (ut) => String(ut.user_type_id) === String(form.user_type_id)
    );
    const isStudent = selectedType && selectedType.user_type_name.toLowerCase().includes("student");

    if (isStudent) {
      url = `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/validate-student/${encodeURIComponent(
        form.entered_id
      )}`;
      label = "Student Roll Number";
    } else {
      url = `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/validate-staff/${encodeURIComponent(
        form.entered_id
      )}`;
      label = "Staff Code";
    }

    try {
      const res = await axios.get(url);
      if (res.data.success && res.data.data) {
        setIdFieldError("");
        const userIdInt = res.data.data.user_id || res.data.data.id || null;
        setForm((prev) => ({ ...prev, user_id: userIdInt }));
        setUserIdValidated(true);
      } else {
        setIdFieldError(`${label} not found.`);
        setForm((prev) => ({ ...prev, user_id: null }));
        setUserIdValidated(false);
        setRules(null);
      }
    } catch (err) {
      setIdFieldError(`${label} not found.`);
      setForm((prev) => ({ ...prev, user_id: null }));
      setUserIdValidated(false);
      setRules(null);
    }
  };

  const handleBookScan = async () => {
    setIsbnFieldError("");

    if (!scanBookIsbn) {
      setIsbnFieldError("Please enter ISBN");
      return;
    }

    if (form.books.find((b) => b.isbn === scanBookIsbn)) {
      setSnackbar({
        open: true,
        message: "Book already added",
        severity: "warning",
      });
      return;
    }

    // Check if book is already issued to this user
    const alreadyIssued = rows.some(
      (row) =>
        row.user_id === form.user_id &&
        row.issue_details?.some((detail) => detail.copy?.book?.isbn === scanBookIsbn)
    );

    if (alreadyIssued) {
      setSnackbar({
        open: true,
        message: "This book is already issued to this user",
        severity: "error",
      });
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/validate-book/${encodeURIComponent(
          scanBookIsbn
        )}`
      );
      if (res.data.success && res.data.data) {
        if (res.data.data.book_status !== "AVAILABLE") {
          setSnackbar({
            open: true,
            message: `Book is ${res.data.data.book_status}. It cannot be issued now.`,
            severity: "error",
          });
          return;
        }

        if (!res.data.data.copy_id) {
          setSnackbar({
            open: true,
            message: "No available copy found for this ISBN.",
            severity: "error",
          });
          return;
        }

        setForm((prev) => ({
          ...prev,
          books: [
            ...prev.books,
            {
              isbn: scanBookIsbn,
              copy_id: res.data.data.copy_id,
              title: res.data.data.book?.title,
              accession_no: res.data.data.accession_no,
              book_status: res.data.data.book_status,
            },
          ],
        }));
        setScanBookIsbn("");
        setIsbnFieldError("");
      } else {
        setIsbnFieldError("Book ISBN not found");
      }
    } catch (err) {
      setIsbnFieldError("Book ISBN not found");
    }
  };

  const handleRemoveBook = (index) => {
    setForm((prev) => ({
      ...prev,
      books: prev.books.filter((_, i) => i !== index),
    }));
  };

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      user_type_id: row.user_type_id || "",
      academic_year: row.academic_year || "",
      issue_type: row.issue_type || "SINGLE",
      entered_id: row.user_id || "",
      user_id: row.user_id || null,
      books: row.books || [],
    });
    setUserIdValidated(true);
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the issue record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it!",
      customClass: {
        popup: "swal2-z-topmost",
      },
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/${row.issue_id}`
      );
      await Swal.fire("Deleted!", "The issue record has been deleted!", "success");
      fetchData();
    } catch (err) {
      await Swal.fire(
        "Sorry!",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    if (!userIdValidated && !editing) {
      setSnackbar({
        open: true,
        message: "Please validate the user ID before submitting.",
        severity: "error",
      });
      return;
    }

    if (form.books.length === 0) {
      setSnackbar({
        open: true,
        message: "Please add at least one book to issue.",
        severity: "error",
      });
      return;
    }

    // Check issue limit
    if (rules && form.books.length > rules.available_quota) {
      setSnackbar({
        open: true,
        message: `Cannot issue ${form.books.length} books. Available quota: ${rules.available_quota}`,
        severity: "error",
      });
      return;
    }

    try {
      const today = new Date();
      const dueDate = rules?.nodays
        ? new Date(today.setDate(today.getDate() + rules.nodays))
        : new Date(today.setDate(today.getDate() + 14));

      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}/${editing.issue_id}`,
          {
            ...form,
            user_type_id: form.user_type_id,
            user_id: form.user_id,
            due_date: dueDate,
            updated_by: user?.user_email || "system",
            updated_on: new Date().toISOString(),
          }
        );
        setSnackbar({ open: true, message: "Updated successfully", severity: "success" });
      } else {
        const detailsPayload = form.books.map((b) => ({
          college_id: collegeId,
          copy_id: b.copy_id,
          book_status: "ISSUED",
          // delete_status: 0, // Removed: not supported by core_mstr_student
          created_by: user?.user_email || "system",
        }));

        const issuePayload = {
          academic_year: form.academic_year,
          issue: {
            user_type_id: parseInt(form.user_type_id),
            user_id: form.user_id,
            issue_type: form.issue_type,
            issue_date: new Date(),
            due_date: dueDate,
            college_id: parseInt(collegeId),
            created_by: user?.user_email || "system",
            // delete_status: 0, // Removed: not supported by core_mstr_student
          },
          details: detailsPayload,
        };

        await axios.post(
          `${API_BASE_URL}/api/library/issue/${clientId}/${collegeId}`,
          issuePayload
        );
        setSnackbar({ open: true, message: "Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  if (idError || isInvalidSession) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {idError || "Session expired or invalid. Please log in again or contact admin."}
        </Alert>
      </Box>
    );
  }

  const filteredRows = Array.isArray(rows) ? rows : [];

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "user_type", headerName: "User Type", flex: 1 },
    { field: "issue_type", headerName: "Issue Type", flex: 1 },
    { field: "user_id", headerName: "User ID", flex: 1 },
    {
      field: "issue_date",
      headerName: "Issue Date",
      flex: 1,
      valueGetter: (params) =>
        params.row.issue_date ? new Date(params.row.issue_date).toLocaleDateString() : "-",
    },
    {
      field: "due_date",
      headerName: "Due Date",
      flex: 1,
      valueGetter: (params) =>
        params.row.due_date ? new Date(params.row.due_date).toLocaleDateString() : "-",
    },
    {
      field: "books",
      headerName: "Books",
      flex: 2,
      valueGetter: (params) =>
        (params.row.books || []).map((b) => b.title || b.isbn || "").join(", "),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Library Book Issue
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ backgroundColor: "#1976d2", color: "#fff", textTransform: "none", boxShadow: 1 }}
            onClick={handleAdd}
          >
            Add Book
          </Button>
        </Toolbar>
        <Box sx={{ height: 500, width: "100%", mt: 2 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            getRowId={(row) => row.issue_id}
          />
        </Box>
      </Paper>

      {/* Add/Edit Issue Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Issue" : "Add New Issue"}</DialogTitle>
        <DialogContent>
          {/* Rules Display Card */}
          <Card sx={{ mb: 3, mt: 1, bgcolor: "#f5f5f5" }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Issue Rules
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Issue Limit:
                  </Typography>
                  <Typography variant="h6">{rules?.issue_limit ?? "-"}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Available Quota:
                  </Typography>
                  <Typography
                    variant="h6"
                    color={rules?.available_quota > 0 ? "success.main" : "error.main"}
                  >
                    {rules?.available_quota ?? "-"}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Due Date:
                  </Typography>
                  <Typography variant="h6">
                    {rules?.nodays ? `${rules.nodays} days from issue` : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Stack spacing={2} mt={1}>
            <TextField
              label="User Type"
              name="user_type_id"
              value={form.user_type_id}
              onChange={handleFormChange}
              select
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option value="">Select User Type</option>
              {userTypes.map((ut) => (
                <option key={ut.user_type_id} value={ut.user_type_id}>
                  {ut.user_type_name}
                </option>
              ))}
            </TextField>

            <TextField
              label={(() => {
                const selectedType = userTypes.find(
                  (ut) => String(ut.user_type_id) === String(form.user_type_id)
                );
                if (selectedType && selectedType.user_type_name.toLowerCase().includes("student")) {
                  return "Student Roll Number";
                } else if (
                  selectedType &&
                  selectedType.user_type_name.toLowerCase().includes("staff")
                ) {
                  return "Staff Code";
                }
                return "User ID";
              })()}
              name="entered_id"
              value={form.entered_id}
              onChange={handleFormChange}
              onBlur={handleIdFieldBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleIdFieldBlur();
                }
              }}
              fullWidth
              required
              error={!!idFieldError}
              helperText={idFieldError || (userIdValidated && "✓ User validated successfully")}
              placeholder="Enter ID and click/tab out to validate"
              sx={{ "& .MuiFormHelperText-root": { color: userIdValidated ? "green" : undefined } }}
            />

            <TextField
              label="Academic Year"
              name="academic_year"
              value={form.academic_year}
              onChange={handleFormChange}
              select
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map((ay) => (
                <option key={ay.acyear_id} value={ay.academic_year}>
                  {ay.academic_year}
                </option>
              ))}
            </TextField>

            <TextField
              label="Issue Type"
              name="issue_type"
              value={form.issue_type}
              onChange={handleFormChange}
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option value="SINGLE">Single</option>
              <option value="BULK">Bulk</option>
            </TextField>

            <Divider>Add Books</Divider>

            {/* Show Add Book input/button only for BULK, or for SINGLE if no book added yet */}

            {(form.issue_type === "BULK" ||
              (form.issue_type === "SINGLE" && form.books.length < 1)) && (
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Book ISBN"
                  value={scanBookIsbn}
                  onChange={(e) => setScanBookIsbn(e.target.value)}
                  fullWidth
                  error={!!isbnFieldError}
                  helperText={isbnFieldError}
                  placeholder="Enter book ISBN"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleBookScan();
                    }
                  }}
                  onBlur={(e) => {
                    // For SINGLE, auto-add book on blur if not already added
                    if (form.issue_type === "SINGLE" && e.target.value) {
                      setScanBookIsbn(e.target.value); // ensure state is up to date
                      setTimeout(() => handleBookScan(), 0); // call after state update
                    }
                  }}
                />
                {form.issue_type === "BULK" && (
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={handleBookScan}
                    sx={{ backgroundColor: "#fff", color: "#000", boxShadow: 1 }}
                  >
                    Add Book
                  </Button>
                )}
              </Stack>
            )}

            {form.issue_type === "SINGLE" && form.books.length >= 1 && (
              <Alert severity="info">Single issue type: Only one book can be added.</Alert>
            )}

            {/* Books List */}
            {form.books.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Books to Issue ({form.books.length}):
                </Typography>
                <Stack spacing={1}>
                  {form.books.map((book, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2">
                            <strong>{book.title || "Book"}</strong>
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ISBN: {book.isbn} | Accession: {book.accession_no || "N/A"}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveBook(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (
                !form.user_type_id ||
                !form.academic_year ||
                !form.issue_type ||
                !form.entered_id ||
                !userIdValidated ||
                form.books.length === 0 ||
                (form.issue_type === "SINGLE" && form.books.length > 1)
              ) {
                alert(
                  `Disabled because:\n` +
                    (!form.user_type_id ? "- User Type is missing\n" : "") +
                    (!form.academic_year ? "- Academic Year is missing\n" : "") +
                    (!form.issue_type ? "- Issue Type is missing\n" : "") +
                    (!form.entered_id ? "- Roll Number/Staff Code is missing\n" : "") +
                    (!userIdValidated ? "- User ID is not validated\n" : "") +
                    (form.books.length === 0 ? "- No books added\n" : "") +
                    (form.issue_type === "SINGLE" && form.books.length > 1
                      ? "- Only one book allowed for Single issue type\n"
                      : "")
                );
                return;
              }
              handleSubmit();
            }}
            disabled={
              !form.user_type_id ||
              !form.academic_year ||
              !form.issue_type ||
              !form.entered_id ||
              !userIdValidated ||
              form.books.length === 0 ||
              (form.issue_type === "SINGLE" && form.books.length > 1) ||
              (rules && rules.available_quota <= 0)
            }
            variant="contained"
            color="primary"
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
